import EmberObject, { computed } from '@ember/object';

import XZoomAccuracyOpening from './x-zoom-accuracy-opening';
import XZoomAccuracyClosing from './x-zoom-accuracy-closing';
import YZoomAccuracyOpening from './y-zoom-accuracy-opening';
import YZoomAccuracyClosing from './y-zoom-accuracy-closing';
import ZoomDirections from './zoom-directions';

///////////////////////////////////////////////////////////////////////////////////////////////////
// Class for handling zooming.                                                                   //
//                                                                                               //
// The idea behind this is to get the current ticks on the chart (including the unlisted ticks)  //
// and see what ticks are included within the bounds. To make the zoomed version more accurate,  //
// this computes for the actual limits and adds it back to the list of limits. The new           //
// one are then used by the graph.                                                               //
///////////////////////////////////////////////////////////////////////////////////////////////////

export default class Zoom extends EmberObject {
  zoomDirections = ZoomDirections.create();
  selectionStartPoint = null;
  selectionDimension = null;
  frame = null;
  xTicks = null;
  yTicks = null;
  limits = null;
  chartHeight = null;
  zoomSettings = null;
  actualOpeningAndClosing = null;
  xTicksInSelection = null;
  yTicksInSelection = null;

  ///////////////////////////////////////////////////////////////////////////////////////////////
  // Computed Values                                                                           //
  ///////////////////////////////////////////////////////////////////////////////////////////////

  @computed(
    'chartHeight',
    'frame',
    'limits.{maxX,minX}',
    'xClosingScaleValue.value',
    'xOpeningScaleValue.value',
    'xTicks',
  )
  get xTicksWithLimits() {
    var xTicks = this.xTicks;
    var firstScaleValue = this.xOpeningScaleValue ? this.xOpeningScaleValue.value : undefined;
    if (!firstScaleValue) {
      firstScaleValue = this.limits.minX;
    }
    var lastScaleValue = this.xClosingScaleValue ? this.xClosingScaleValue.value : undefined;
    if (!lastScaleValue) {
      lastScaleValue = this.limits.maxX;
    }
    var frameBbox = this.frame.getBoundingClientRect();
    var openingScaleValue = { value: firstScaleValue, x: 0, y: this.chartHeight };
    var closingScaleValue = { value: lastScaleValue, x: frameBbox.width, y: this.chartHeight };
    var xTickResult = [openingScaleValue];
    var result = xTickResult.concat(xTicks);
    result.push(closingScaleValue);
    return result;
  }

  @computed('frame', 'limits.{maxY,minY}', 'yClosingScaleValue.value', 'yOpeningScaleValue.value', 'yTicks')
  get yTicksWithLimits() {
    var yTicks = this.yTicks;
    var firstScaleValue = this.yOpeningScaleValue ? this.yOpeningScaleValue.value : undefined;
    if (!firstScaleValue) {
      firstScaleValue = this.limits.maxY;
    }
    var lastScaleValue = this.yClosingScaleValue ? this.yClosingScaleValue.value : undefined;
    if (!lastScaleValue) {
      lastScaleValue = this.limits.minY;
    }
    var frameBbox = this.frame.getBoundingClientRect();
    var openingScaleValue = { value: firstScaleValue, x: 0, y: 0 };
    var closingScaleValue = { value: lastScaleValue, x: 0, y: frameBbox.height };
    var yTickResult = [closingScaleValue];
    var result = yTickResult.concat(yTicks);
    result.push(openingScaleValue);
    return result;
  }

  @computed('xTicksInSelection', 'yTicksInSelection') get zoomLimits() {
    var xTicksInSelection = this.xTicksInSelection;
    var yTicksInSelection = this.yTicksInSelection;
    var result = {};
    if (yTicksInSelection && xTicksInSelection && yTicksInSelection.length > 1 && xTicksInSelection.length > 1) {
      result = {
        minX: parseFloat(xTicksInSelection[0].value),
        maxX: parseFloat(xTicksInSelection[xTicksInSelection.length - 1].value),
        minY: parseFloat(yTicksInSelection[0].value),
        maxY: parseFloat(yTicksInSelection[yTicksInSelection.length - 1].value),
      };
    }
    return result;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////
  // Callable Functions                                                                        //
  ///////////////////////////////////////////////////////////////////////////////////////////////

  // Sets the first point of the selection area to be zoomed on mouse down
  startZoomSelect(event, elem) {
    if (window.getSelection()) {
      window.getSelection().removeAllRanges();
    } else if (document.selection) {
      document.selection.empty();
    }
    var frameBox = elem.getBoundingClientRect();
    var scrollX = window.scrollX || document.documentElement.scrollLeft;
    var scrollY = window.scrollY || document.documentElement.scrollTop;
    var selectionStartPoint = { x: event.pageX - (frameBox.left + scrollX), y: event.pageY - (frameBox.top + scrollY) };
    var result = null;
    if (
      selectionStartPoint.x >= 0 &&
      selectionStartPoint.y >= 0 &&
      selectionStartPoint.x <= frameBox.width &&
      selectionStartPoint.y <= frameBox.height
    ) {
      result = selectionStartPoint;
      this.set('zoomDirections.startingPosition', result);
      this.set('selectionStartPoint', result);
      this.set('frame', elem);
    }
    return result;
  }

  // Getting selection area while mouse is moving
  moveZoomSelect(event) {
    var elem = this.frame;
    var xPos = event.pageX;
    var yPos = event.pageY;
    var scrollX = window.scrollX || document.documentElement.scrollLeft;
    var scrollY = window.scrollY || document.documentElement.scrollTop;
    var offsetLeft = elem.getBoundingClientRect().left + scrollX;
    var offsetTop = elem.getBoundingClientRect().top + scrollY;
    var currentPosition = { x: xPos - offsetLeft, y: yPos - offsetTop };
    var rectangleCoordinate = this.zoomDirections.getRectangleCoordinates(currentPosition);
    var frameBox = elem.getBoundingClientRect();
    var frameDimension = { width: frameBox.width, height: frameBox.height };
    if (rectangleCoordinate) {
      if (rectangleCoordinate.width > frameDimension.width - rectangleCoordinate.x) {
        rectangleCoordinate.width = frameDimension.width - rectangleCoordinate.x;
      } else if (rectangleCoordinate.width < 0) {
        rectangleCoordinate.width = 0;
      }
      if (rectangleCoordinate.height > frameDimension.height - rectangleCoordinate.y) {
        rectangleCoordinate.height = frameDimension.height - rectangleCoordinate.y;
      } else if (rectangleCoordinate.height < 0) {
        rectangleCoordinate.height = 0;
      }
      var selectionDimension = { width: rectangleCoordinate.width, height: rectangleCoordinate.height };
      var selectionStartPoint = { x: rectangleCoordinate.x, y: rectangleCoordinate.y };
      this.set('selectionDimension', selectionDimension);
      this.set('selectionStartPoint', selectionStartPoint);
      return selectionDimension;
    }
  }

  // Sets the last point of the selection area to be zoomed on mouse up
  stopZoomSelect() {
    var selectionStartPoint = this.selectionStartPoint;
    var selectionDimension = this.selectionDimension;

    if (this.selectionStartPoint.x < 0) {
      this.set('selectionStartPoint.x', 0);
    }
    if (this.selectionStartPoint.y < 0) {
      this.set('selectionStartPoint.y', 0);
    }

    var frame = this.frame;
    var zoomSettings = null;
    if (selectionDimension && selectionStartPoint) {
      zoomSettings = {
        x: selectionStartPoint.x,
        y: selectionStartPoint.y,
        width: selectionDimension.width,
        height: selectionDimension.height,
      };
      var frameWidth = parseFloat(frame.attributes['width'].value);
      var frameHeight = parseFloat(frame.attributes['height'].value);
      if (zoomSettings.x + zoomSettings.width > frameWidth) {
        zoomSettings.width = frameWidth - zoomSettings.x;
      }
      if (zoomSettings.y + zoomSettings.height > frameHeight) {
        zoomSettings.height = frameHeight - zoomSettings.x;
      }
    } else {
      return;
    }
    this.set('zoomSettings', zoomSettings);
    var xTicksWithLimits = this.xTicksWithLimits;
    var yTicksWithLimits = this.yTicksWithLimits;

    var actualXOpening = XZoomAccuracyOpening.create({ ticksWithLimits: xTicksWithLimits, zoomSettings: zoomSettings });
    var actualXClosing = XZoomAccuracyClosing.create({ ticksWithLimits: xTicksWithLimits, zoomSettings: zoomSettings });
    var actualYOpening = YZoomAccuracyOpening.create({ ticksWithLimits: yTicksWithLimits, zoomSettings: zoomSettings });
    var actualYClosing = YZoomAccuracyClosing.create({ ticksWithLimits: yTicksWithLimits, zoomSettings: zoomSettings });

    var actualOpeningAndClosing = {
      actualXOpening: actualXOpening,
      actualXClosing: actualXClosing,
      actualYOpening: actualYOpening,
      actualYClosing: actualYClosing,
    };
    this.set('actualOpeningAndClosing', actualOpeningAndClosing);
    this.setZoomSelection();
  }

  // Sets the Zoom Selection Area
  setZoomSelection() {
    var zoomSettings = this.zoomSettings;
    var xTicks = this.xTicksWithLimits;
    var xTicksInSelection = xTicks.filter(function (item) {
      if (item.x >= zoomSettings.x && item.x <= zoomSettings.x + zoomSettings.width) {
        return item;
      }
    });
    var startXZoom = {
      value: this.actualOpeningAndClosing?.actualXOpening?.actualEdgeValue,
      x: zoomSettings.x,
      y: this.chartHeight,
    };
    var endXZoom = {
      value: this.actualOpeningAndClosing?.actualXClosing?.actualEdgeValue,
      x: zoomSettings.x + zoomSettings.width,
      y: this.chartHeight,
    };
    this.set('xOpeningScaleValue', startXZoom);
    this.set('xClosingScaleValue', endXZoom);
    var xZoom = [startXZoom];
    xZoom = xZoom.concat(xTicksInSelection);
    xZoom.push(endXZoom);
    this.set('xTicksInSelection', xZoom);
    var yTicks = this.yTicksWithLimits;
    var yTicksInSelection = yTicks.filter(function (item) {
      if (item.y >= zoomSettings.y && item.y <= zoomSettings.y + zoomSettings.height) {
        return item;
      }
    });
    var startYZoom = { value: this.actualOpeningAndClosing?.actualYOpening?.actualEdgeValue, x: 0, y: zoomSettings.y };
    var endYZoom = {
      value: this.actualOpeningAndClosing?.actualYClosing?.actualEdgeValue,
      x: 0,
      y: zoomSettings.y + zoomSettings.height,
    };
    this.set('yClosingScaleValue', endYZoom);
    this.set('yOpeningScaleValue', startYZoom);
    var yZoom = [endYZoom];
    yZoom = yZoom.concat(yTicksInSelection);
    yZoom.push(startYZoom);
    this.set('yTicksInSelection', yZoom);
  }

  // Clears the Zoom Selection Area
  clearZoomSelection() {
    this.set('selectionStartPoint', null);
    this.set('selectionDimension', null);
  }

  // Resets Zooming
  returnTo100() {
    this.set('xClosingScaleValue', null);
    this.set('xOpeningScaleValue', null);
    this.set('yOpeningScaleValue', null);
    this.set('yClosingScaleValue', null);
  }
}
